import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * El avatar ve a la persona (opcional, solo si activa la camara).
 *
 * La imagen de la camara se analiza DENTRO del navegador con MediaPipe (cara y
 * manos). No sale del equipo, no se graba y no se envia a ningun servidor: al
 * avatar solo le llegan unas pocas senales, y con ellas reacciona:
 *
 *   - donde esta la persona  -> la sigue con la cabeza
 *   - si sonrie               -> le devuelve la sonrisa
 *   - si asiente              -> asiente con ella
 *   - si saluda con la mano   -> sonrie, levanta las cejas y la saluda
 *   - pulgar arriba           -> asiente y sonrie
 *
 * Los modelos y el motor se sirven desde el propio dominio (/mediapipe/).
 */

export interface UserCue {
  /** Hay una cara delante. */
  present: boolean;
  /** Posicion de la persona vista desde el avatar: -1 izquierda de la pantalla, +1 derecha. */
  x: number;
  /** -1 arriba, +1 abajo. */
  y: number;
  /** 0..1 */
  smile: number;
  /** Contadores de gestos: cada vez que cambian, hubo uno nuevo. */
  nods: number;
  waves: number;
  thumbs: number;
  /** performance.now() de la ultima medida. */
  t: number;
}

export type CameraState = 'off' | 'starting' | 'on' | 'denied' | 'error';

interface Handlers {
  /** La persona aparece delante (tras no haber nadie unos segundos). */
  onArrive?: () => void;
  onWave?: () => void;
}

export function useUserCues(handlers: Handlers) {
  const cueRef = useRef<UserCue | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<CameraState>('off');
  const stopRef = useRef<(() => void) | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const stop = useCallback(() => {
    stopRef.current?.();
    stopRef.current = null;
    cueRef.current = null;
    setState('off');
  }, []);

  const start = useCallback(async () => {
    if (stopRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('error');
      return;
    }
    setState('starting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
    } catch (error) {
      setState((error as DOMException)?.name === 'NotAllowedError' ? 'denied' : 'error');
      return;
    }

    let cancelled = false;
    let timer = 0;
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.srcObject = stream;
    videoRef.current = video;
    const closeAll: Array<() => void> = [() => stream.getTracks().forEach((t) => t.stop())];
    stopRef.current = () => {
      cancelled = true;
      window.clearTimeout(timer);
      closeAll.forEach((f) => {
        try {
          f();
        } catch {
          /* ya cerrado */
        }
      });
      videoRef.current = null;
    };

    try {
      await video.play();
      const vision = await import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks('/mediapipe');
      // En la CPU (XNNPACK): en la GPU competiria con el giro de la cabeza,
      // que tambien usa WebGL, y la animacion perderia fluidez.
      const make = async <T,>(create: (delegate: 'GPU' | 'CPU') => Promise<T>) => {
        try {
          return await create('CPU');
        } catch {
          return await create('GPU');
        }
      };
      const face = await make((delegate) =>
        vision.FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: '/mediapipe/face_landmarker.task', delegate },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFaceBlendshapes: true,
        }),
      );
      closeAll.push(() => face.close());
      const hands = await make((delegate) =>
        vision.GestureRecognizer.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: '/mediapipe/gesture_recognizer.task', delegate },
          runningMode: 'VIDEO',
          numHands: 1,
        }),
      ).catch(() => null);
      if (hands) closeAll.push(() => hands.close());
      if (cancelled) return;
      setState('on');

      // --- Deteccion de gestos a partir de las medidas ----------------------
      const cue: UserCue = { present: false, x: 0, y: 0, smile: 0, nods: 0, waves: 0, thumbs: 0, t: performance.now() };
      cueRef.current = cue;
      let absentSince = performance.now() - 10_000;
      let lastNod = 0;
      let lastWave = 0;
      let lastThumb = 0;
      let palmHits = 0;
      // El analisis corre en el mismo hilo que la animacion: se ajusta el
      // ritmo para no llevarse mas de ~30 % del tiempo, sea cual sea el equipo.
      let cost = 20;
      const pitchHist: Array<{ t: number; v: number }> = [];
      let frame = 0;

      const tick = () => {
        if (cancelled) return;
        const now = performance.now();
        frame += 1;
        if (video.readyState >= 2) {
          const t0 = performance.now();
          const res = face.detectForVideo(video, now);
          const lm = res.faceLandmarks?.[0];
          if (lm) {
            if (!cue.present && now - absentSince > 4000) handlersRef.current.onArrive?.();
            cue.present = true;
            const nose = lm[1]!;
            // La imagen de la camara no esta en espejo: lo que la persona tiene
            // a su izquierda sale a la derecha de la imagen.
            cue.x += (-(nose.x - 0.5) * 2 - cue.x) * 0.35;
            cue.y += ((nose.y - 0.5) * 2 - cue.y) * 0.35;
            const bs = res.faceBlendshapes?.[0]?.categories ?? [];
            const score = (name: string) => bs.find((c) => c.categoryName === name)?.score ?? 0;
            cue.smile += ((score('mouthSmileLeft') + score('mouthSmileRight')) / 2 - cue.smile) * 0.4;
            // Asentir: la nariz baja y sube respecto a los ojos (independiente de
            // lo lejos que este la persona) en menos de un segundo.
            const eyeY = (lm[33]!.y + lm[263]!.y) / 2;
            const chinY = lm[152]!.y;
            const v = (nose.y - eyeY) / Math.max(0.01, chinY - eyeY);
            pitchHist.push({ t: now, v });
            while (pitchHist.length && now - pitchHist[0]!.t > 1100) pitchHist.shift();
            if (pitchHist.length > 5 && now - lastNod > 1500) {
              const vs = pitchHist.map((h) => h.v);
              const lo = Math.min(...vs);
              const hi = Math.max(...vs);
              const iHi = vs.indexOf(hi);
              const settled = Math.abs(vs[vs.length - 1]! - vs[0]!) < (hi - lo) * 0.45;
              if (hi - lo > 0.07 && settled && iHi > 0 && iHi < vs.length - 1) {
                cue.nods += 1;
                lastNod = now;
                pitchHist.length = 0;
              }
            }
          } else {
            if (cue.present) absentSince = now;
            cue.present = false;
          }

          // Manos, una medida de cada tres (es el modelo mas pesado).
          if (hands && frame % 3 === 0) {
            const g = hands.recognizeForVideo(video, now).gestures?.[0]?.[0];
            const name = g && g.score > 0.6 ? g.categoryName : '';
            if (name === 'Open_Palm') {
              palmHits += 1;
              if (palmHits >= 2 && now - lastWave > 3500) {
                cue.waves += 1;
                lastWave = now;
                handlersRef.current.onWave?.();
              }
            } else {
              palmHits = 0;
            }
            if (name === 'Thumb_Up' && now - lastThumb > 3000) {
              cue.thumbs += 1;
              cue.nods += 1;
              lastThumb = now;
            }
          }
          cue.t = now;
          cost += (performance.now() - t0 - cost) * 0.2;
        }
        timer = window.setTimeout(tick, Math.max(90, Math.min(600, cost * 2.3)));
      };
      tick();
    } catch (error) {
      console.warn('[camara] no se pudo iniciar el analisis:', error);
      stopRef.current?.();
      stopRef.current = null;
      if (!cancelled) setState('error');
    }
  }, []);

  useEffect(() => () => stopRef.current?.(), []);

  const toggle = useCallback(() => {
    if (stopRef.current) stop();
    else void start();
  }, [start, stop]);

  return { cueRef, videoRef, state, toggle, stop };
}
